import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, DollarSign, Clock, CheckCircle, 
  Building2, ArrowUpRight, Calendar, Percent,
  Loader2, AlertCircle, Download, FileText,
  ChevronDown, ChevronRight, MapPin,
  Handshake, Eye, ThumbsUp, ThumbsDown, Play,
  Upload, ShieldAlert, Paperclip
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../contexts/AuthContext';
import api, { dealFlowApi, investorApi } from '../lib/api';
import { generateInvestorStatement } from '../lib/pdfGenerator';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Penny-exact variant — used where the investor needs reconciliation precision
// (e.g. their Subdivide Master cap position, lot basis, and closing wires).
const formatCurrencyExact = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  // Handle date-only strings (YYYY-MM-DD) to avoid timezone shift
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day); // Local time
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

import { StatusBadge } from '../components/StatusBadge';

const getTierColor = (tier) => {
  const colors = {
    1: 'text-green-400',
    2: 'text-yellow-400',
    3: 'text-orange-400',
    4: 'text-red-400',
    5: 'text-red-600'
  };
  return colors[tier] || 'text-gray-400';
};

export const InvestorDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingStatement, setDownloadingStatement] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});

  const toggleExpand = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  // Available Deals state
  const [availableDeals, setAvailableDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [pofRequired, setPofRequired] = useState(false);
  const [uploadingPof, setUploadingPof] = useState(false);
  const pofInputRef = useRef(null);
  const [activePortalTab, setActivePortalTab] = useState('portfolio');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [acceptingDeal, setAcceptingDeal] = useState(null);
  const [decliningDeal, setDecliningDeal] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineDealId, setDeclineDealId] = useState(null);
  // Accept confirmation dialog state
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [acceptDealId, setAcceptDealId] = useState(null);
  // Multi-partner: amount an "open" partner proposes to fund (Phase 2)
  const [proposedCapital, setProposedCapital] = useState('');
  // Partner profile state
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [editingCapital, setEditingCapital] = useState(false);
  const [capitalInput, setCapitalInput] = useState('');
  const [savingCapital, setSavingCapital] = useState(false);

  const loadPartnerProfile = async () => {
    try {
      const res = await dealFlowApi.getProfile();
      if (res.data) {
        setPartnerProfile(res.data);
        setCapitalInput(res.data.total_available_capital?.toString() || '0');
      }
    } catch (err) {
      // Not a partner — that's fine
    }
  };

  const handleSaveCapital = async () => {
    setSavingCapital(true);
    try {
      const res = await dealFlowApi.updateProfile({
        total_available_capital: parseFloat(String(capitalInput).replace(/[,$]/g, '')) || 0,
      });
      setPartnerProfile(res.data);
      setEditingCapital(false);
      toast.success('Available capital updated');
    } catch (err) {
      toast.error('Failed to update capital');
    } finally {
      setSavingCapital(false);
    }
  };

  useEffect(() => {
    loadPartnerProfile();
  }, []);

  const loadAvailableDeals = async () => {
    setDealsLoading(true);
    try {
      const res = await dealFlowApi.availableDeals();
      const data = res.data;
      if (data && data.pof_required) {
        setPofRequired(true);
        setAvailableDeals([]);
      } else {
        setPofRequired(false);
        setAvailableDeals(data?.deals || data || []);
      }
    } catch (err) {
      // Partner may not have any deals — not an error
    } finally {
      setDealsLoading(false);
    }
  };

  useEffect(() => {
    loadAvailableDeals();
  }, []);

  // ── Refresh "My Investments" data after acceptance so the new deal
  //    appears in the portfolio tab without a manual page reload.
  const reloadPortfolioData = async () => {
    try {
      const response = await api.get('/analytics/investor-dashboard');
      setData(response.data);
    } catch (err) {
      // Non-fatal — the toast/success state already covers UX feedback.
    }
  };

  const handleAcceptDeal = async (dealId) => {
    const d = availableDeals.find(x => x.id === dealId) || selectedDeal;
    const isOpenMulti = !!d?.multi_partner && ((d?.my_allotment?.basis) || 'open') === 'open';
    setAcceptingDeal(dealId);
    try {
      const payload = isOpenMulti
        ? { proposed_capital: Number(String(proposedCapital).replace(/[,$]/g, '')) || 0 }
        : {};
      const res = await dealFlowApi.acceptDeal(dealId, payload);
      const pending = res?.data?.contract?.status === 'pending_owner_confirmation';
      toast.success(pending
        ? 'Your proposed amount was submitted. Land Finder will confirm your allocation shortly.'
        : 'Congratulations! Your contract is being drafted and will be emailed shortly.');
      setShowAcceptDialog(false);
      setAcceptDealId(null);
      setSelectedDeal(null);
      setProposedCapital('');
      // Refresh both lists, then redirect partner to My Investments
      // so they can see the newly committed deal in their portfolio.
      await Promise.all([loadAvailableDeals(), reloadPortfolioData()]);
      if (!pending) setActivePortalTab('portfolio');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept deal');
    } finally {
      setAcceptingDeal(null);
    }
  };

  const handleDeclineDeal = async () => {
    if (!declineDealId) return;
    setDecliningDeal(declineDealId);
    try {
      await dealFlowApi.declineDeal(declineDealId, { reason: declineReason });
      toast.success('Deal declined');
      setShowDeclineDialog(false);
      setDeclineReason('');
      setDeclineDealId(null);
      loadAvailableDeals();
      setSelectedDeal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    } finally {
      setDecliningDeal(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/analytics/investor-dashboard');
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Download investor statement for a project
  const downloadStatement = async (projectId, projectName) => {
    setDownloadingStatement(projectId);
    try {
      // Fetch capital position data for the project
      const response = await api.get(`/projects/${projectId}/capital-position`);
      const waterfallData = response.data;
      
      // Fetch project details
      const projectResponse = await api.get(`/projects/${projectId}`);
      const project = projectResponse.data;
      
      // Find the JV agreement from the dashboard data
      const investment = [...(data.active_investments || []), ...(data.completed_investments || [])]
        .find(inv => inv.project_id === projectId);
      
      // Create JV agreement object for PDF generation
      const jvAgreement = {
        investor_name: data.investor_name,
        capital_contributed: investment?.capital_contributed || waterfallData.jv_capital
      };
      
      // Generate and download the PDF
      generateInvestorStatement(project, waterfallData, jvAgreement);
    } catch (err) {
      console.error('Failed to download statement:', err);
      alert('Failed to generate statement. Please try again.');
    } finally {
      setDownloadingStatement(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-3xl font-light text-forest-900 tracking-tight">
          Welcome, {user?.name || data?.investor_name}
        </h1>
        <p className="text-xs sm:text-base text-forest-600 mt-1">Your investment portfolio overview</p>
      </div>

      {/* Tabs: Portfolio & Available Deals */}
      <Tabs value={activePortalTab} onValueChange={setActivePortalTab}>
        <TabsList className="bg-forest-800 border border-forest-700">
          <TabsTrigger value="portfolio" className="data-[state=active]:bg-forest-700 data-[state=active]:text-white text-white/60" data-testid="tab-portfolio">
            <Building2 className="w-4 h-4 mr-1.5" /> My Investments
          </TabsTrigger>
          <TabsTrigger value="deals" className="data-[state=active]:bg-forest-700 data-[state=active]:text-white text-white/60 relative" data-testid="tab-available-deals">
            <Handshake className="w-4 h-4 mr-1.5" /> Available Deals
            {availableDeals.filter(d => !d.application).length > 0 && (
              <span className="ml-1.5 w-5 h-5 rounded-full bg-[#C68E5D] text-white text-[10px] font-bold flex items-center justify-center">
                {availableDeals.filter(d => !d.application).length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Portfolio Tab */}
        <TabsContent value="portfolio" className="space-y-8 mt-6">
          <PortfolioContent
            data={data}
            expandedProjects={expandedProjects}
            toggleExpand={toggleExpand}
            downloadStatement={downloadStatement}
            downloadingStatement={downloadingStatement}
            onSwitchToDeals={() => setActivePortalTab('deals')}
          />
        </TabsContent>

        {/* Available Deals Tab */}
        <TabsContent value="deals" className="space-y-6 mt-6">
          {/* Partner Profile Card */}
          {partnerProfile && (
            <Card className="bg-forest-800 border-forest-700" data-testid="partner-profile-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase text-white/40 tracking-wide">Your Available Capital</div>
                    {editingCapital ? (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-white/60 text-sm">$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={capitalInput}
                          onChange={e => setCapitalInput(e.target.value)}
                          className="bg-forest-700 border border-forest-600 text-white rounded-sm px-2 py-1 w-40 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#C68E5D]"
                          autoFocus
                          data-testid="capital-input"
                        />
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-[#C68E5D] hover:bg-[#C68E5D]/90 text-white rounded-sm"
                          onClick={handleSaveCapital}
                          disabled={savingCapital}
                          data-testid="save-capital-btn"
                        >
                          {savingCapital ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-white/50 hover:text-white rounded-sm"
                          onClick={() => { setEditingCapital(false); setCapitalInput(partnerProfile.total_available_capital?.toString() || '0'); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xl font-bold text-[#C68E5D] font-mono" data-testid="current-capital">
                          {formatCurrency(partnerProfile.total_available_capital)}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-white/40 hover:text-white rounded-sm"
                          onClick={() => setEditingCapital(true)}
                          data-testid="edit-capital-btn"
                        >
                          Update
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-white">{partnerProfile.name}</div>
                    <div className="text-xs text-white/50">{partnerProfile.entity_name || partnerProfile.email}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {dealsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
            </div>
          ) : pofRequired ? (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-8">
                <div className="text-center">
                  <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">Proof of Funds Required</h3>
                  <p className="text-amber-700 mb-4 max-w-md mx-auto">To receive investment opportunities, please upload a Proof of Funds document (bank statement, line of credit, or financial institution letter).</p>
                  <input
                    ref={pofInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
                      setUploadingPof(true);
                      try {
                        await investorApi.uploadPof(file);
                        toast.success('Proof of Funds uploaded successfully!');
                        setPofRequired(false);
                        // Reload deals
                        const res = await dealFlowApi.availableDeals();
                        const data = res.data;
                        setPofRequired(data?.pof_required || false);
                        setAvailableDeals(data?.deals || data || []);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Upload failed');
                      } finally {
                        setUploadingPof(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    onClick={() => pofInputRef.current?.click()}
                    disabled={uploadingPof}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="pof-upload-btn"
                  >
                    {uploadingPof ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {uploadingPof ? 'Uploading...' : 'Upload Proof of Funds'}
                  </Button>
                  <p className="text-xs text-amber-600 mt-2">PDF, JPG, PNG, DOC — max 10MB</p>
                </div>
              </CardContent>
            </Card>
          ) : availableDeals.length === 0 ? (
            <Card className="bg-forest-800 border-forest-700">
              <CardContent className="py-12">
                <div className="text-center">
                  <Handshake className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Available Deals</h3>
                  <p className="text-white/60">New investment opportunities will appear here when they&apos;re available.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {availableDeals.map(deal => (
                <AvailableDealCard
                  key={deal.id}
                  deal={deal}
                  onViewDetails={() => setSelectedDeal(deal)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Deal Detail Dialog */}
      <Dialog open={!!selectedDeal} onOpenChange={() => setSelectedDeal(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="deal-detail-dialog">
          {selectedDeal && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">{selectedDeal.summary?.project_name || 'Deal Details'}</DialogTitle>
              </DialogHeader>
              <DealDetailContent
                deal={selectedDeal}
                onAccept={() => { setAcceptDealId(selectedDeal.id); setProposedCapital(String(selectedDeal?.capital_remaining ?? selectedDeal?.investor_capital_target ?? '')); setShowAcceptDialog(true); }}
                accepting={acceptingDeal === selectedDeal.id}
                onDecline={() => { setDeclineDealId(selectedDeal.id); setShowDeclineDialog(true); }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decline Reason Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle>Decline Deal</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground mb-3">Optionally share why you're declining:</p>
            <textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="Reason (optional)..."
              className="w-full border rounded-sm px-3 py-2 text-sm h-20 resize-none"
              data-testid="decline-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeclineDialog(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={handleDeclineDeal}
              disabled={decliningDeal}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              data-testid="decline-confirm-btn"
            >
              {decliningDeal ? 'Declining...' : 'Decline Deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept Confirmation Dialog — single-step commitment confirmation */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="sm:max-w-md" data-testid="accept-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-emerald-600" /> Confirm Acceptance
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {(() => {
              const d = availableDeals.find(x => x.id === acceptDealId) || selectedDeal;
              const s = d?.summary || {};
              const allotBasis = (d?.my_allotment?.basis) || 'open';
              const isMulti = !!d?.multi_partner;
              const isOpenMulti = isMulti && allotBasis === 'open';
              const target = d?.investor_capital_target || s.total_investor_capital || 0;
              const remaining = d?.capital_remaining ?? target;
              const assignedAmt = allotBasis === 'percent' && d?.my_allotment?.value
                ? target * (Number(d.my_allotment.value) / 100)
                : allotBasis === 'capital' && d?.my_allotment?.value
                  ? Number(d.my_allotment.value)
                  : null;
              return (
                <>
                  {isOpenMulti ? (
                    <>
                      <p className="text-sm text-foreground">
                        You&apos;re joining <strong>{s.project_name || 'this deal'}</strong>. How much capital would you like to fund?
                      </p>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Your proposed capital (USD)</label>
                        <input
                          type="number"
                          min="0"
                          value={proposedCapital}
                          onChange={(e) => setProposedCapital(e.target.value)}
                          className="mt-1 w-full h-9 border rounded-sm px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          data-testid="accept-proposed-capital-input"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Up to <strong>{formatCurrency(remaining)}</strong> remaining of the {formatCurrency(target)} target. Land Finder will confirm your final allocation before drafting your agreement.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-foreground">
                        You&apos;re about to accept <strong>{s.project_name || 'this deal'}</strong>
                        {assignedAmt != null
                          ? <> for your assigned slice of <strong>{formatCurrency(assignedAmt)}</strong>{d?.my_allotment?.basis === 'percent' ? <> ({d.my_allotment.value}% of the investor side)</> : null}.</>
                          : s.total_investor_capital ? <> for <strong>{formatCurrency(s.total_investor_capital)}</strong> capital.</> : <>.</>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Once you confirm, our team will draft the JV agreement and email it to you for signature shortly. This action commits you to the deal.
                      </p>
                    </>
                  )}
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowAcceptDialog(false); setAcceptDealId(null); }}
              className="rounded-sm"
              data-testid="accept-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => acceptDealId && handleAcceptDeal(acceptDealId)}
              disabled={!!acceptingDeal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1.5"
              data-testid="accept-confirm-btn"
            >
              <ThumbsUp className="w-4 h-4" /> {acceptingDeal ? 'Accepting...' : 'Confirm & Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═══════════════════════════════════════════════
// Portfolio Content (extracted from original dashboard)
// ═══════════════════════════════════════════════
function PortfolioContent({ data, expandedProjects, toggleExpand, downloadStatement, downloadingStatement, onSwitchToDeals }) {
  if (!data) return null;

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <Card className="bg-forest-800 border-forest-700" data-testid="cash-invested-card">
          <CardContent className="p-3 sm:pt-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-white/60">Cash Invested</p>
                <p className="text-lg sm:text-2xl font-semibold text-white mt-1" data-testid="cash-invested-value">
                  {formatCurrency(data?.total_cash_invested ?? data?.total_capital_deployed)}
                </p>
                <p className="text-[10px] text-white/40 mt-1">
                  Actual cash you've wired in
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-terracotta/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-terracotta" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="p-3 sm:pt-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-white/60">Total Returns</p>
                <p className="text-lg sm:text-2xl font-semibold text-white mt-1">
                  {formatCurrency(data?.total_return)}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="p-3 sm:pt-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-white/60">Profit Received</p>
                <p className="text-lg sm:text-2xl font-semibold text-green-400 mt-1">
                  {formatCurrency(data?.total_profit_received)}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="p-3 sm:pt-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-white/60">Overall ROI</p>
                <p className="text-lg sm:text-2xl font-semibold text-white mt-1">
                  {data?.summary?.total_roi_percentage?.toFixed(1)}%
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Percent className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Investment Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{data?.summary?.active_count || 0}</p>
                <p className="text-sm text-white/60">Active Investments</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">{data?.summary?.completed_count || 0}</p>
                <p className="text-sm text-white/60">Completed Deals</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-terracotta/20 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-terracotta" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-white">
                  {(data?.summary?.active_count || 0) + (data?.summary?.completed_count || 0)}
                </p>
                <p className="text-sm text-white/60">Total Investments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accepted — Awaiting Setup */}
      {data?.pending_investments?.length > 0 && (
        <Card className="bg-forest-800 border-[#C68E5D]/40" data-testid="pending-investments-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C68E5D]" />
              Accepted — Awaiting Setup
            </CardTitle>
            <CardDescription>
              You&apos;ve committed to {data.pending_investments.length} deal{data.pending_investments.length !== 1 ? 's' : ''}. Your JV agreement is being prepared and will be emailed for signature.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.pending_investments.map((inv) => (
                <div
                  key={inv.deal_id}
                  className="p-4 bg-forest-700/50 rounded-lg border border-[#C68E5D]/20"
                  data-testid={`pending-investment-${inv.deal_id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-white">{inv.project_name}</h3>
                        <Badge className="bg-[#C68E5D]/20 text-[#C68E5D] text-[10px] border-[#C68E5D]/30">
                          {inv.signing_status === 'sent' || inv.signing_status === 'contract_sent'
                            ? 'Contract Sent — Awaiting Signature'
                            : 'Accepted — Contract In Progress'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                        <div>
                          <div className="text-[10px] uppercase text-white/40 tracking-wide">Capital Committed</div>
                          <div className="text-sm font-semibold text-[#C68E5D]">{formatCurrency(inv.capital_committed)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-white/40 tracking-wide">Purchase Price</div>
                          <div className="text-sm font-semibold text-white">{formatCurrency(inv.purchase_price)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-white/40 tracking-wide">Projected ROI</div>
                          <div className="text-sm font-semibold text-green-400">{(inv.projected_roi || 0).toFixed(1)}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Investments */}
      {data?.active_investments?.length > 0 && (
        <Card className="bg-forest-800 border-forest-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Active Investments
            </CardTitle>
            <CardDescription>Properties currently in progress</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.active_investments.map((investment) => (
                <div key={investment.project_id}>
                  <Link
                    to={`/projects/${investment.project_id}`}
                    className="block"
                  >
                    <div className="p-4 bg-forest-700/50 rounded-lg hover:bg-forest-700 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-medium text-white">{investment.project_name}</h3>
                            <StatusBadge status={investment.status} />
                            {investment.is_subdivision && (
                              <Badge variant="outline" className="border-blue-400/50 text-blue-400 text-xs">
                                {investment.child_lots.length} Lot{investment.child_lots.length !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-4 text-sm text-white/60">
                            <span className="flex items-center gap-1" title="Cash you have wired in to this deal">
                              <DollarSign className="w-4 h-4" />
                              {formatCurrency((investment.balance_sheet?.investor_cash_in) ?? investment.cash_contributed ?? investment.capital_contributed)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {investment.days_held} days held
                            </span>
                            {investment.current_tier && (
                              <span className={`flex items-center gap-1 ${getTierColor(investment.current_tier)}`}>
                                Tier {investment.current_tier} ({(investment.investor_split * 100).toFixed(0)}% split)
                              </span>
                            )}
                          </div>

                          {/* Wave 4 — BALANCE-SHEET PER INVESTMENT.
                              Always shown (no conditional gate), so every
                              investment card is consistent. Same model as
                              the project page: Cash In + Net Owed for each
                              party, with reconciliation strip. */}
                          {investment.balance_sheet && (
                            <div className="mt-3 bg-forest-900/40 border border-forest-700 rounded-sm p-3" data-testid={`investor-balance-sheet-${investment.project_id}`}>
                              <div className="text-[10px] uppercase tracking-wide text-white/50 mb-2">Your Balance on This Deal — Dual-Ledger View</div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                                <div>
                                  <div className="text-[10px] text-white/40">Cash Paid <span className="italic">(Capital Flow Ledger)</span></div>
                                  <div className="text-sm font-semibold text-emerald-300" data-testid={`bs-cash-in-${investment.project_id}`}>
                                    {formatCurrency(investment.balance_sheet.investor_cash_in || 0)}
                                  </div>
                                  <div className="text-[10px] text-white/40 mt-0.5">
                                    Actual wires + your-paid expenses
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-white/40">Contractual Hurdle <span className="italic">(Legal Mandate Ledger)</span></div>
                                  <div className="text-sm font-semibold text-white" data-testid={`bs-hurdle-${investment.project_id}`}>
                                    {formatCurrency(investment.balance_sheet.investor_responsibility || 0)}
                                  </div>
                                  <div className="text-[10px] text-white/40 mt-0.5">
                                    Per JV split %
                                  </div>
                                </div>
                                {(() => {
                                  const net = investment.balance_sheet.investor_net_owed || 0;
                                  const owesMoney = net > 0.005;
                                  const isOwed = net < -0.005;
                                  const cls = owesMoney ? 'text-amber-400' : isOwed ? 'text-cyan-300' : 'text-white';
                                  const subLabel = owesMoney ? 'You still owe at settlement' : isOwed ? 'Owed back to you at settlement' : 'Reconciled';
                                  return (
                                    <div>
                                      <div className="text-[10px] text-white/40">Net Partner Position</div>
                                      <div className={`text-sm font-semibold ${cls}`} data-testid={`bs-net-owed-${investment.project_id}`}>
                                        {formatCurrency(Math.abs(net))}
                                      </div>
                                      <div className="text-[10px] text-white/40 mt-0.5">
                                        {subLabel}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                              {/* LF advance pending — only shown when there's
                                  an actual pending settlement, since most
                                  active deals have $0 here. */}
                              {investment.pending_owed_to_manager !== 0 && (
                                <div className="mt-2 pt-2 border-t border-forest-700/60 text-[11px] text-white/60">
                                  {investment.pending_owed_to_manager > 0
                                    ? <>Land Finder fronted <strong className="text-amber-400">{formatCurrency(Math.abs(investment.pending_owed_to_manager))}</strong> on your behalf — repaid at sale settlement (Manager Preferred Reimbursement).</>
                                    : <>Land Finder owes you <strong className="text-cyan-300">{formatCurrency(Math.abs(investment.pending_owed_to_manager))}</strong> from over-payment — paid back at sale settlement.</>
                                  }
                                </div>
                              )}
                              {/* Senior LF emergency advance on this deal —
                                  shown only when there's actually a senior
                                  outlay (rare). Investors should know the
                                  manager is repaid SENIOR to their hurdle. */}
                              {(investment.balance_sheet.lf_emergency_advance || 0) > 0.005 && (
                                <div className="mt-2 pt-2 border-t border-rose-700/40 text-[11px] text-rose-200">
                                  Land Finder funded <strong>{formatCurrency(investment.balance_sheet.lf_emergency_advance)}</strong> in senior emergency outlays on this deal — repaid <strong>SENIOR</strong> to your hurdle at sale settlement.
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── YOUR SHARE OF THE POOLED JV (co-investor) ──
                              When the deal's investor side is funded by several
                              named people under one JV, show ONLY the logged-in
                              investor's slice, allocated by funded share. */}
                          {investment.my_investor_split && !investment.is_subdivide_master && (
                            <div className="mt-3 bg-forest-900/40 border border-emerald-700/40 rounded-sm p-3" data-testid={`my-investor-split-${investment.project_id}`}>
                              <div className="text-[10px] uppercase tracking-wide text-emerald-300/80 mb-2">Your Share of This Pooled JV</div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                                <div>
                                  <div className="text-[10px] text-white/40">Your Funded</div>
                                  <div className="text-sm font-semibold text-white">
                                    {formatCurrency(investment.my_investor_split.funded || investment.my_investor_split.capital_committed)}
                                  </div>
                                  <div className="text-[10px] text-white/40 mt-0.5">{investment.my_investor_split.funded_pct}% of investor side</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-white/40">Your Profit Share</div>
                                  <div className="text-sm font-semibold text-emerald-300" data-testid={`my-investor-profit-${investment.project_id}`}>
                                    {formatCurrency(investment.my_investor_split.allocated_profit)}
                                  </div>
                                  <div className="text-[10px] text-white/40 mt-0.5">Projected — finalizes at sale</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-white/40">Your Total Return</div>
                                  <div className="text-sm font-semibold text-white">
                                    {formatCurrency(investment.my_investor_split.total_return)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Next-tier countdown only — no speculative payout/profit values.
                              Investors see projections only after the deal actually profits. */}
                          {investment.days_until_next_tier != null && investment.next_tier_split && (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                              <div className="bg-forest-900/40 border border-forest-700 rounded-sm p-2">
                                <div className="text-[10px] uppercase text-white/40 tracking-wide">Next tier</div>
                                <div className="text-sm font-semibold text-amber-400">
                                  {investment.days_until_next_tier} days remaining
                                </div>
                                <div className="text-[10px] text-white/50 mt-0.5">
                                  splits to {(investment.next_tier_split * 100).toFixed(0)}% investor share
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Milestone timeline removed in Wave 4 — the
                              status badge above already conveys deal stage,
                              and the timeline strip ate vertical space without
                              adding info. Keeping the per-deal balance-sheet
                              and next-tier countdown gives investors the only
                              two metrics that actually change day-to-day. */}
                        </div>
                        <ArrowUpRight className="w-5 h-5 text-white/40 flex-shrink-0" />
                      </div>
                    </div>
                  </Link>

                  {/* Subdivision Child Lots */}
                  {investment.is_subdivision && investment.child_lots.length > 0 && (
                    <div className="ml-4 mt-1">
                      <button
                        onClick={() => toggleExpand(investment.project_id)}
                        className="flex items-center gap-2 text-sm text-white/60 hover:text-white/80 transition-colors py-2 px-2"
                        data-testid={`toggle-lots-${investment.project_id}`}
                      >
                        {expandedProjects[investment.project_id] 
                          ? <ChevronDown className="w-4 h-4" /> 
                          : <ChevronRight className="w-4 h-4" />
                        }
                        <MapPin className="w-4 h-4" />
                        View {investment.child_lots.length} Subdivided Lot{investment.child_lots.length !== 1 ? 's' : ''}
                      </button>
                      
                      {expandedProjects[investment.project_id] && (
                        <div className="space-y-2 pl-2 border-l-2 border-forest-600 ml-2">
                          {investment.child_lots.map((lot) => (
                            <Link
                              key={lot.project_id}
                              to={`/projects/${lot.project_id}`}
                              className="block"
                            >
                              <div className="p-3 bg-forest-700/30 rounded-lg hover:bg-forest-700/50 transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <span className="text-sm font-medium text-white/90">{lot.name}</span>
                                    <StatusBadge status={lot.status} />
                                    {lot.acreage && (
                                      <span className="text-xs text-white/40">{Number(lot.acreage).toFixed(2)} acres</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-white/50">
                                    {lot.sale_price ? (
                                      <span className="text-green-400">
                                        Sold: {formatCurrency(lot.sale_price)}
                                        {lot.sale_date && (
                                          <span className="text-white/40 ml-1">
                                            ({new Date(lot.sale_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                                          </span>
                                        )}
                                      </span>
                                    ) : lot.estimated_sale_price ? (
                                      <span>Est. Sale: {formatCurrency(lot.estimated_sale_price)}</span>
                                    ) : lot.purchase_price ? (
                                      <span>Cost: {formatCurrency(lot.purchase_price)}</span>
                                    ) : null}
                                    <ArrowUpRight className="w-4 h-4 text-white/30" />
                                  </div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Subdivide Master — lot matrix + per-closing payouts (your slice) */}
                  {investment.is_subdivide_master && (
                    <div className="ml-4 mt-2 space-y-3" data-testid={`investor-subdivide-${investment.project_id}`}>
                      {investment.subdivide_cap && (
                        <div className="p-3 bg-forest-700/30 rounded-lg text-sm">
                          <p className="text-[10px] uppercase tracking-wide text-white/50 mb-2">Capital Position</p>
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-white/80">
                            <span>Funded: <strong>{formatCurrencyExact(investment.subdivide_cap.investor.funded)}</strong></span>
                            <span>Responsibility: <strong>{formatCurrencyExact(investment.subdivide_cap.investor.hurdle)}</strong></span>
                            <span className={investment.subdivide_cap.investor.rebalance >= 0 ? 'text-emerald-300' : 'text-amber-300'}>
                              Rebalance: <strong>{formatCurrencyExact(investment.subdivide_cap.investor.rebalance)}</strong>
                            </span>
                          </div>
                        </div>
                      )}
                      {investment.subdivide_lots?.length > 0 && (
                        <div className="p-3 bg-forest-700/30 rounded-lg" data-testid={`investor-lot-matrix-${investment.project_id}`}>
                          <p className="text-[10px] uppercase tracking-wide text-white/50 mb-2">Lots</p>
                          <div className="space-y-1">
                            {investment.subdivide_lots.map((lot) => (
                              <div key={lot.lot_id} className="flex items-center justify-between text-sm text-white/80">
                                <span className="flex items-center gap-2">
                                  {lot.name}
                                  <span className="text-xs text-white/40">{Number(lot.acres).toFixed(2)} ac</span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${lot.status === 'sold' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/50'}`}>{lot.status}</span>
                                </span>
                                <span>{formatCurrencyExact(lot.cost_basis)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {investment.closings?.length > 0 && (
                        <div className="p-3 bg-forest-700/30 rounded-lg" data-testid={`investor-closings-${investment.project_id}`}>
                          <p className="text-[10px] uppercase tracking-wide text-white/50 mb-2">Closings &amp; Your Payouts</p>
                          <div className="space-y-2">
                            {investment.closings.map((c, i) => {
                              // Prefer the investor's individual co-investor slice
                              // (pooled JV) when present; else the partner wire.
                              const w = c.my_coinvestor_wire || c.my_wire;
                              return (
                              <div key={i} className="text-sm border-t border-white/10 pt-2 first:border-0 first:pt-0">
                                <div className="flex justify-between text-white/80">
                                  <span>{c.closing_date} · resale {formatCurrencyExact(c.gross_resale_price)}</span>
                                  <span className="font-semibold text-emerald-300" data-testid={`investor-wire-${investment.project_id}-${i}`}>
                                    Your wire: {formatCurrencyExact(w?.total_wire || 0)}
                                  </span>
                                </div>
                                {w && (
                                  <div className="text-xs text-white/40 mt-0.5">
                                    Capital back {formatCurrencyExact(w.tier2_capital_return)} + Profit {formatCurrencyExact(w.tier3_profit)}
                                    {w.tier1_rebalance ? ` + Rebalance ${formatCurrencyExact(w.tier1_rebalance)}` : ''}
                                    {' · '}{c.investor_split_pct}% split
                                    {c.my_coinvestor_wire ? ` · your ${c.my_coinvestor_wire.funded_pct}% funded slice` : ''}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Investments */}
      {data?.completed_investments?.length > 0 && (
        <Card className="bg-forest-800 border-forest-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Completed Investments
            </CardTitle>
            <CardDescription>Deals that have closed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="w-full min-w-[850px]">
                <thead>
                  <tr className="text-left text-sm text-white/60 border-b border-forest-600">
                    <th className="pb-3 font-medium">Property</th>
                    <th className="pb-3 font-medium">Capital</th>
                    <th className="pb-3 font-medium">Sale Price</th>
                    <th className="pb-3 font-medium">Your Profit</th>
                    <th className="pb-3 font-medium">Total Return</th>
                    <th className="pb-3 font-medium">CoC Return</th>
                    <th className="pb-3 font-medium">Ann. ROI</th>
                    <th className="pb-3 font-medium">Days Held</th>
                    <th className="pb-3 font-medium text-center">Statement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-forest-700">
                  {data.completed_investments.map((investment) => (
                    <React.Fragment key={investment.project_id}>
                      <tr className="text-sm">
                        <td className="py-3">
                          <Link 
                            to={`/projects/${investment.project_id}`}
                            className="text-white hover:text-terracotta transition-colors"
                          >
                            {investment.project_name}
                          </Link>
                          <div className="text-xs text-white/40 mt-0.5">
                            Sold {formatDate(investment.sale_date)}
                          </div>
                          {investment.is_subdivision && (
                            <button
                              onClick={() => toggleExpand(investment.project_id)}
                              className="flex items-center gap-1 mt-1 text-xs text-blue-400 hover:text-blue-300"
                              data-testid={`toggle-completed-lots-${investment.project_id}`}
                            >
                              {expandedProjects[investment.project_id] 
                                ? <ChevronDown className="w-3 h-3" /> 
                                : <ChevronRight className="w-3 h-3" />
                              }
                              {investment.child_lots.length} lot{investment.child_lots.length !== 1 ? 's' : ''}
                            </button>
                          )}
                        </td>
                        <td className="py-3 text-white/80">{formatCurrency(investment.capital_contributed)}</td>
                        <td className="py-3 text-white/80">{formatCurrency(investment.sale_price)}</td>
                        <td className="py-3 text-green-400">{formatCurrency(investment.investor_profit)}</td>
                        <td className="py-3 text-white font-medium">{formatCurrency(investment.total_return)}</td>
                        <td className="py-3">
                          <span className={investment.cash_on_cash_return >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {investment.cash_on_cash_return !== undefined ? `${investment.cash_on_cash_return >= 0 ? '+' : ''}${investment.cash_on_cash_return?.toFixed(1)}%` : '-'}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className={investment.annualized_roi >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {investment.annualized_roi !== undefined ? `${investment.annualized_roi >= 0 ? '+' : ''}${investment.annualized_roi?.toFixed(1)}%` : '-'}
                          </span>
                        </td>
                        <td className="py-3 text-white/60">{investment.days_held}</td>
                        <td className="py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadStatement(investment.project_id, investment.project_name)}
                            disabled={downloadingStatement === investment.project_id}
                            className="text-terracotta hover:text-terracotta hover:bg-terracotta/10"
                            data-testid={`download-statement-${investment.project_id}`}
                          >
                            {downloadingStatement === investment.project_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                      {investment.is_subdivision && expandedProjects[investment.project_id] && (
                        <tr>
                          <td colSpan={9} className="py-0 pb-3">
                            <div className="ml-4 pl-3 border-l-2 border-forest-600 space-y-1">
                              {investment.child_lots.map((lot) => (
                                <Link
                                  key={lot.project_id}
                                  to={`/projects/${lot.project_id}`}
                                  className="flex items-center justify-between p-2 bg-forest-700/30 rounded hover:bg-forest-700/50 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-3 h-3 text-white/30" />
                                    <span className="text-xs text-white/80">{lot.name}</span>
                                    <StatusBadge status={lot.status} />
                                  </div>
                                  {lot.sale_price && (
                                    <span className="text-xs text-green-400">{formatCurrency(lot.sale_price)}</span>
                                  )}
                                </Link>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Investments - Download Statements */}
      {data?.active_investments?.length > 0 && (
        <Card className="bg-forest-800 border-forest-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              Capital Position Statements
            </CardTitle>
            <CardDescription>Download current position statements for active investments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.active_investments.map((investment) => (
                <div
                  key={investment.project_id}
                  className="p-4 bg-forest-700/50 rounded-lg border border-forest-600"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-white">{investment.project_name}</h4>
                      <p className="text-xs text-white/60">{investment.days_held} days held</p>
                    </div>
                    <StatusBadge status={investment.status} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/80" title="Cash you have wired in to this deal">
                      {formatCurrency((investment.balance_sheet?.investor_cash_in) ?? investment.cash_contributed ?? investment.capital_contributed)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadStatement(investment.project_id, investment.project_name)}
                      disabled={downloadingStatement === investment.project_id}
                      className="border-terracotta/50 text-terracotta hover:bg-terracotta/10"
                      data-testid={`download-position-${investment.project_id}`}
                    >
                      {downloadingStatement === investment.project_id ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Download className="w-4 h-4 mr-1" />
                      )}
                      Statement
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {(!data?.active_investments?.length && !data?.completed_investments?.length && !data?.pending_investments?.length) && (
        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="py-12">
            <div className="text-center">
              <Building2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Investments Yet</h3>
              <p className="text-white/60">Your JV investments will appear here once they&apos;re set up.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════
// Available Deal Card
// ═══════════════════════════════════════════════
function AvailableDealCard({ deal, onViewDetails }) {
  const s = deal.summary || {};
  const app = deal.application;

  const statusBadge = () => {
    if (!app) return <Badge className="bg-amber-100 text-amber-800 text-[10px]">New</Badge>;
    switch (app.status) {
      // Legacy states (one-step flow no longer creates these, but old data
      // pre-dating the flow change may still show them).
      case 'applied': return <Badge className="bg-blue-100 text-blue-800 text-[10px]">Under Review</Badge>;
      case 'approved': return <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Ready to Accept</Badge>;
      case 'declined_by_admin': return <Badge className="bg-red-100 text-red-700 text-[10px]">Not Selected</Badge>;
      case 'contract_sent': return <Badge className="bg-purple-100 text-purple-800 text-[10px]">Contract Sent</Badge>;
      default: return null;
    }
  };

  return (
    <Card
      className="bg-forest-800 border-forest-700 overflow-hidden cursor-pointer transition-colors hover:bg-forest-800/70"
      data-testid={`deal-card-${deal.id}`}
      onClick={onViewDetails}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h3 className="font-medium text-white text-base">{s.project_name || s.proposal_name || 'Investment Opportunity'}</h3>
              {statusBadge()}
              {deal.deal_status === 'committed' && app?.status !== 'accepted' && (
                <Badge className="bg-gray-600 text-gray-200 text-[10px]">Closed</Badge>
              )}
            </div>
            {s.description && <p className="text-sm text-white/60 mb-3">{s.description}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <div className="text-[10px] uppercase text-white/40 tracking-wide">Purchase Price</div>
                <div className="text-sm font-semibold text-white">{formatCurrency(s.purchase_price)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-white/40 tracking-wide">Est. Sale</div>
                <div className="text-sm font-semibold text-white">{formatCurrency(s.estimated_sale_price)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-white/40 tracking-wide">Capital Required</div>
                <div className="text-sm font-semibold text-[#C68E5D]">{formatCurrency(s.total_investor_capital)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-white/40 tracking-wide">Projected ROI</div>
                <div className="text-sm font-semibold text-green-400">{s.projected_roi?.toFixed(1)}%</div>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onViewDetails(); }}
              className="mt-3 text-xs text-[#C68E5D] hover:underline inline-flex items-center gap-1"
              data-testid={`view-full-${deal.id}`}
            >
              <Eye className="w-3 h-3" /> View full proposal details
              {Array.isArray(deal.attachments) && deal.attachments.length > 0 && (
                <span
                  className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[#C68E5D]/20 text-[#C68E5D] rounded text-[10px] font-medium"
                  data-testid={`deal-attachment-badge-${deal.id}`}
                >
                  <Paperclip className="w-2.5 h-2.5" />
                  {deal.attachments.length} PDF
                </span>
              )}
            </button>
          </div>

          <div className="flex flex-col gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {deal.deal_status === 'open' && (!app || app.status === 'applied' || app.status === 'approved') && (
              <Button
                onClick={(e) => { e.stopPropagation(); onViewDetails(); }}
                className="bg-[#C68E5D] hover:bg-[#C68E5D]/90 text-white rounded-sm gap-1.5 text-xs"
                data-testid={`view-details-${deal.id}`}
              >
                <Eye className="w-3 h-3" /> Review & Respond
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════
// Deal Detail Content (single-step Accept / Decline)
// ═══════════════════════════════════════════════
function DealDetailContent({ deal, onAccept, accepting, onDecline }) {
  const s = deal.summary || {};
  const fd = deal.full_details || {};
  const d = fd.deal || {};
  const f = fd.funding || {};
  const app = deal.application;
  const selectedScenario = fd.selected_scenario || 'A';
  const tiers = selectedScenario === 'B' ? (fd.option_b_tiers || []) : (fd.option_a_tiers || []);

  // Use full_details numbers when available, fall back to summary so the
  // dialog still shows meaningful info if a deal pre-dates full_details.
  const purchasePrice = d.purchasePrice ?? s.purchase_price ?? 0;
  const estSalePrice = d.estimatedSalePrice ?? s.estimated_sale_price ?? 0;
  const totalDD = (d.survey || 0) + (d.soil || 0) + (d.otherDD || 0);
  const purchaseClosingCosts = d.purchaseClosingCosts || 0;
  const improvements = d.improvements || 0;
  const totalAcq = (purchasePrice || 0) + totalDD + purchaseClosingCosts + improvements;
  // Sale-side deductions (must be shown so the Gross Profit math is traceable)
  const saleCommissionPct = d.saleCommission || 0;
  const saleCommissionAmt = (estSalePrice || 0) * (saleCommissionPct / 100);
  const saleClosingCosts = d.saleClosingCosts || 0;
  const netSaleProceeds = (estSalePrice || 0) - saleCommissionAmt - saleClosingCosts;

  return (
    <div className="space-y-5 py-2">
      {/* Summary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Purchase Price', value: formatCurrency(purchasePrice), color: 'text-foreground' },
          { label: 'Est. Sale Price', value: formatCurrency(estSalePrice), color: 'text-foreground' },
          { label: 'Gross Profit', value: formatCurrency(s.gross_profit), color: 'text-green-500' },
          { label: 'Projected ROI', value: `${s.projected_roi?.toFixed(1)}%`, color: 'text-green-500' },
        ].map(m => (
          <div key={m.label} className="bg-muted/30 rounded-sm p-3">
            <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{m.label}</div>
            <div className={`text-base font-bold ${m.color}`}>{m.value}</div>
          </div>
        ))}
      </div>

      <Separator />

      {/* Profit Walk — full Sale → Gross Profit derivation, fully transparent */}
      <div>
        <h4 className="text-sm font-semibold mb-2">Profit Walk</h4>
        <div className="text-sm space-y-1.5" data-testid="investor-profit-walk">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Estimated Sale Price</span>
            <span className="font-mono" data-testid="walk-sale-price">{formatCurrency(estSalePrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Less: Sale Commission{saleCommissionPct ? ` (${saleCommissionPct}%)` : ''}</span>
            <span className="font-mono text-red-500" data-testid="walk-sale-commission">({formatCurrency(saleCommissionAmt)})</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Less: Sale Closing Costs</span>
            <span className="font-mono text-red-500" data-testid="walk-sale-closing">({formatCurrency(saleClosingCosts)})</span>
          </div>
          <Separator className="my-1" />
          <div className="flex justify-between font-semibold">
            <span>Net Sale Proceeds</span>
            <span className="font-mono" data-testid="walk-net-proceeds">{formatCurrency(netSaleProceeds)}</span>
          </div>
          <div className="flex justify-between pt-1">
            <span className="text-muted-foreground">Less: Purchase Price</span>
            <span className="font-mono text-red-500">({formatCurrency(purchasePrice)})</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Less: Due Diligence</span>
            <span className="font-mono text-red-500">({formatCurrency(totalDD)})</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Less: Purchase Closing Costs</span>
            <span className="font-mono text-red-500">({formatCurrency(purchaseClosingCosts)})</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Less: Improvements</span>
            <span className="font-mono text-red-500">({formatCurrency(improvements)})</span>
          </div>
          <Separator className="my-1" />
          <div className="flex justify-between font-semibold">
            <span>Total Acquisition</span>
            <span className="font-mono" data-testid="walk-total-acq">{formatCurrency(totalAcq)}</span>
          </div>
          <Separator className="my-1" />
          <div className="flex justify-between text-base font-bold text-green-600 pt-1">
            <span>Gross Profit</span>
            <span className="font-mono" data-testid="walk-gross-profit">{formatCurrency(s.gross_profit)}</span>
          </div>
        </div>
      </div>

      <Separator />

      {/* Funding Structure */}
      <div>
        <h4 className="text-sm font-semibold mb-2">Funding Structure</h4>
        <div className="text-sm space-y-1.5">
          <div className="flex justify-between"><span className="text-muted-foreground">Investor — Purchase Price</span><span className="font-mono">{100 - (f.lfPurchasePrice || 0)}%</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Investor — DD Costs</span><span className="font-mono">{100 - (f.lfDDCosts || 0)}%</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Investor — Improvements</span><span className="font-mono">{100 - (f.lfImprovements || 0)}%</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Investor — Closing Costs</span><span className="font-mono">{100 - (f.lfClosingCosts || 0)}%</span></div>
          <Separator className="my-1" />
          <div className="flex justify-between font-semibold text-[#C68E5D]"><span>Your Capital Required</span><span className="font-mono">{formatCurrency(s.total_investor_capital)}</span></div>
        </div>
      </div>

      {/* Profit Split Tiers */}
      {tiers.length > 0 && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold mb-2">Profit Split Tiers</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-muted-foreground">
                    <th className="pb-2">Holding Period</th>
                    <th className="pb-2 text-right">Your Split</th>
                    <th className="pb-2 text-right">LF Split</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((t, i) => (
                    <tr key={i} className="border-t border-border/30">
                      <td className="py-1.5">{t.startDay || t.minDays}–{t.endDay || t.maxDays} days</td>
                      <td className="py-1.5 text-right font-semibold text-green-600">{t.investorSplit}%</td>
                      <td className="py-1.5 text-right">{100 - t.investorSplit}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Video */}
      {deal.video_url && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Play className="w-4 h-4" /> Deal Video</h4>
            <a href={deal.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-500 hover:underline">
              {deal.video_url}
            </a>
          </div>
        </>
      )}

      {/* Attachments (PDFs uploaded by admin when promoting) — server
          auth-gates the download endpoint, so investors can re-download
          anytime from the portal without relying on the original email. */}
      {Array.isArray(deal.attachments) && deal.attachments.length > 0 && (
        <>
          <Separator />
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Paperclip className="w-4 h-4" /> Attached Documents
            </h4>
            <ul className="space-y-1.5" data-testid={`deal-attachments-${deal.id}`}>
              {deal.attachments.map(a => (
                <li key={a.id}>
                  <a
                    href={dealFlowApi.attachmentDownloadUrl(deal.id, a.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-muted/40 hover:bg-muted/60 border rounded-sm text-sm transition-colors"
                    data-testid={`deal-attachment-link-${a.id}`}
                  >
                    <Paperclip className="w-4 h-4 text-[#C68E5D] flex-shrink-0" />
                    <span className="flex-1 truncate font-medium">{a.filename}</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {!a.size ? '' : a.size < 1024 ? `${a.size} B` : a.size < 1024 * 1024 ? `${(a.size / 1024).toFixed(1)} KB` : `${(a.size / 1024 / 1024).toFixed(1)} MB`}
                    </span>
                    <Download className="w-3.5 h-3.5 text-muted-foreground" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Accept / Decline — one-step commitment. Shown whenever the deal is
          still open AND the partner hasn't already responded. Legacy
          `applied` / `approved` states also funnel through this same UI so
          old data stays actionable after the flow change. */}
      {deal.deal_status === 'open' && (!app || app.status === 'applied' || app.status === 'approved') && (
        <>
          <Separator />
          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={onAccept}
              disabled={accepting}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1.5"
              data-testid="accept-deal-btn"
            >
              <ThumbsUp className="w-4 h-4" /> {accepting ? 'Accepting...' : 'Accept Deal'}
            </Button>
            <Button
              onClick={onDecline}
              variant="outline"
              className="flex-1 rounded-sm gap-1.5 text-red-600 border-red-300 hover:bg-red-50"
              data-testid="decline-deal-btn"
            >
              <ThumbsDown className="w-4 h-4" /> Decline
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            Accepting will initiate the JV agreement. A contract will be emailed to you for signature shortly.
          </p>
        </>
      )}
    </div>
  );
}
